import type {
	ErrorResponse,
	ErrorStatuses,
	FetchResponse,
	FetchResponseOfError,
} from "Infrastructure/Api/Api";
import { captureException } from "@sentry/react";
import type {
	QueryFunctionContext,
	QueryKey,
	UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";

export type ErrorDetailDTO = {
	code: string;
	message: string;
};

export type ExceptionDTO = {
	errors?: { [key: string | number]: string } | null;
	type?: string | null;
	title?: string | null;
	status?: number | null;
	detail?: string | null;
	instance?: string | null;
	//portalStackTrace?: { [key: string | number]: ExceptionStackTraceItemDTO[] } | null;
	//stackTrace?: { [key: string | number]: ExceptionStackTraceItemDTO[] } | null;
};

type BaseResponse<TData = unknown> =
	| FetchResponse<TData, number>
	| ErrorResponse;

type SuccessResponse<TResponse extends BaseResponse> = Extract<
	TResponse,
	{ status: 200 | 201 | 202 | 204 }
>;

type SuccessResponseData<TResponse extends BaseResponse> =
	SuccessResponse<TResponse>["data"];

export type Statuses400s = 400 | 401 | 403 | 404 | 405 | 409 | 422;

export type ProcessResponseOptions = {
	skipToastFor?: (ErrorStatuses | Statuses400s)[] | true;
};

export type QueryInfo = {
	meta?: UseQueryOptions["meta"];
	queryKey: QueryKey;
};
export class ApiCallError extends Error {
	data: FetchResponseOfError | FetchResponse<ExceptionDTO, Statuses400s>;
	queryInfo?: QueryInfo;
	options?: ProcessResponseOptions;

	constructor(
		message: string,
		data: FetchResponseOfError,
		options?: ProcessResponseOptions,
		queryKey?: QueryInfo["queryKey"],
		meta?: QueryInfo["meta"],
	) {
		super(message);
		this.name = "ApiCallError";
		this.data = data;
		this.options = options;
		if (queryKey) {
			this.queryInfo = { queryKey, meta };
		}
	}

	protected isApiException = true;
}

// biome-ignore lint/suspicious/noExplicitAny: "none"
export const isFetchResponseOfError = (obj: any): obj is FetchResponseOfError =>
	!!obj.data && typeof obj.status === "number";

// biome-ignore lint/suspicious/noExplicitAny: "none"
export const handleResponseError = (error: any) => {
	logExceptionToSentry(error);
	const skipToast =
		error instanceof ApiCallError
			? error.options?.skipToastFor === true ||
				error.options?.skipToastFor?.includes(error.data.status)
			: false;

	const status = error instanceof ApiCallError ? error.data.status : "";

	const errorMessage =
		error.data?.data?.message ??
		error.error ??
		error.message ??
		"Unknown error";

	if (status === 403) {
		toast.warning("You don't have permissions to this resource", {
			id: error.error,
		});
		return;
	}

	if (status === 400 || status === 401) {
		return;
	}

	if (errorMessage && !skipToast) {
		toast.error(`${status} ${errorMessage}`, { id: error.error });
	}
};

// biome-ignore lint/suspicious/noExplicitAny: "none"
function logExceptionToSentry(error: any) {
	captureException(error, (scope) => {
		// Set basic error information
		scope.setLevel("error");

		if (error instanceof ApiCallError) {
			// Set transaction name for API calls
			const transactionName =
				error.data.args?.options?.method && error.data.args?.url
					? [error.data.args?.options?.method ?? "", error.data.args?.url]
							.filter((a) => !!a)
							.join(" ")
					: "Unknown API Call";

			scope.setTransactionName(transactionName);

			// Set tags for better filtering in Sentry
			scope.setTag("errorType", "ApiCallError");
			scope.setTag("httpMethod", error.data.args?.options?.method || "unknown");
			scope.setTag("httpStatus", error.data.status?.toString() || "unknown");
			scope.setTag("endpoint", error.data.args?.url || "unknown");

			// Set request context
			scope.setContext("request", {
				method: error.data.args?.options?.method,
				url: error.data.args?.url,
				status: error.data.status,
			});

			// Set response context
			if (error.data.data) {
				scope.setContext("response", {
					data: error.data.data,
					status: error.data.status,
				});
			}

			// Capture the original error message
			if (error.message) {
				scope.setExtra("originalErrorMessage", error.message);
			}

			// Capture parsed error message from response
			const responseMessage = getMessageFromErrorResponse(error);
			if (responseMessage) {
				scope.setExtra("responseErrorMessage", responseMessage);
			}

			// Special handling for "Not Found" errors
			if (error.data.status === 404 || responseMessage?.includes("Not Found")) {
				scope.setTag("errorCategory", "NotFound");
				scope.setExtra("notFoundDetails", {
					requestedUrl: error.data.args?.url,
					method: error.data.args?.options?.method,
					timestamp: new Date().toISOString(),
				});
			}

			// Special handling for other common HTTP errors
			if (error.data.status === 401) {
				scope.setTag("errorCategory", "Unauthorized");
			} else if (error.data.status === 403) {
				scope.setTag("errorCategory", "Forbidden");
			} else if (error.data.status === 400) {
				scope.setTag("errorCategory", "BadRequest");
			} else if (error.data.status >= 500) {
				scope.setTag("errorCategory", "ServerError");
			}

			// Capture detailed error information
			scope.setExtra("errorDetails", {
				name: error.name,
				message: error.message,
				stack: error.stack,
				status: error.data.status,
				url: error.data.args?.url,
				method: error.data.args?.options?.method,
			});

			// Capture query information if available
			if (error.queryInfo) {
				scope.setExtra("queryInfo", error.queryInfo);
			}

			// Capture full error data for debugging
			if (error.data) {
				scope.setExtra("data", error.data);
			}
		} else {
			// Handle non-ApiCallError instances
			scope.setTag("errorType", error?.constructor?.name || "UnknownError");

			if (error?.message) {
				scope.setExtra("errorMessage", error.message);
			}

			if (error?.stack) {
				scope.setExtra("errorStack", error.stack);
			}

			// Capture full error object for unknown errors
			scope.setExtra("fullError", {
				name: error?.name,
				message: error?.message,
				stack: error?.stack,
			});
		}

		return scope;
	});
}

// biome-ignore lint/suspicious/noExplicitAny: "none"
export function getMessageFromErrorResponse(response: any | undefined) {
	let errorMessage: string | undefined;
	let responseError = response;
	if (responseError instanceof ApiCallError) {
		responseError = responseError.data;
	}

	if (isFetchResponseOfError(responseError)) {
		responseError = responseError.data;
	}

	//if has property title
	if (responseError?.title?.length > 0 && responseError?.detail?.length > 0) {
		return `${responseError.title}: ${responseError.detail}`;
	}

	if (responseError?.title?.length > 0) {
		return responseError.title;
	}

	if (responseError instanceof Error) {
		errorMessage = responseError.message;
	}

	//Return the message if it exists
	if (responseError?.message) {
		return responseError.message;
	}

	return errorMessage ?? "Unknown error";
}

export const processResponseBase =
	<TArgs, TResponse extends BaseResponse>(
		request: (args: TArgs, headers?: Headers) => Promise<TResponse>,
	) =>
	async (args: TArgs): Promise<TResponse> => {
		const response = await request(args);
		return processResponseStatusCodeBase(response);
	};

/**
 *  Process response and throw error if status code is not 2xx
 * @param request request function
 * @returns Processed response
 */
export const processResponse =
	<TArgs, TResponse extends BaseResponse>(
		request: (args: TArgs, headers?: Headers) => Promise<TResponse>,
		options?: ProcessResponseOptions,
	) =>
	async (args: TArgs): Promise<SuccessResponseData<TResponse>> => {
		const response = await request(args);
		return processResponseStatusCode(response, options);
	};

/**
 *  Process response and throw error if status code is not 2xx
 * @param request request function
 * @returns Processed response
 */
export const processResponseQuery =
	<TResponse extends BaseResponse, TQueryKey extends QueryKey = QueryKey>(
		request: (
			args: QueryFunctionContext<TQueryKey>,
			headers?: Headers,
		) => Promise<TResponse>,
		options?: ProcessResponseOptions,
	) =>
	async (
		args: QueryFunctionContext<TQueryKey>,
	): Promise<SuccessResponseData<TResponse>> => {
		const response = await request(args);
		return processResponseStatusCode(response, options, args);
	};

export const processResponseAndExtractData =
	<
		TArgs,
		TResponse extends BaseResponse,
		TExtract extends keyof SuccessResponseData<TResponse>,
	>(
		request: (args: TArgs, headers?: Headers) => Promise<TResponse>,
		propertyKey: TExtract,
	) =>
	async (args: TArgs): Promise<SuccessResponseData<TResponse>[TExtract]> => {
		const response = await processResponse(request)(args);
		return response[propertyKey];
	};

function processResponseStatusCodeBase<
	TResponse extends BaseResponse,
	TQueryKey extends QueryKey = QueryKey,
>(
	response: TResponse,
	options?: ProcessResponseOptions,
	args?: QueryFunctionContext<TQueryKey, never>,
): TResponse {
	if (response?.status >= 400 || response?.status === 0) {
		const message = getMessageFromErrorResponse(response);

		if (response?.status !== 404) {
			throw new ApiCallError(
				message,
				response as FetchResponseOfError,
				options,
				args?.queryKey,
				args?.meta,
			);
		}

		throw new ApiCallError(
			"Not Found",
			response as unknown as FetchResponseOfError,
		);
	}

	return response;
}

function processResponseStatusCode<
	TResponse extends BaseResponse,
	TQueryKey extends QueryKey = QueryKey,
>(
	response: TResponse,
	options?: ProcessResponseOptions,
	args?: QueryFunctionContext<TQueryKey, never>,
): SuccessResponseData<TResponse> {
	return processResponseStatusCodeBase(response, options, args)?.data;
}

export type HandleCallResponse<TData> =
	| {
			isSuccess: true;
			data: TData;
	  }
	| { isSuccess: false; data?: never };

/**
 * Similar to @function processResponse but used to handle manual api calls. It does not throw an error but shows a toast message when status is not 2xx.
 * @param request request function
 * @returns Processed response
 */
export const handleCall = async <TResponse extends BaseResponse>(
	request: Promise<TResponse>,
	options?: ProcessResponseOptions,
): Promise<HandleCallResponse<SuccessResponseData<TResponse>>> => {
	try {
		const response = await request;
		const data = processResponseStatusCode(response, options);
		return { isSuccess: true, data };
	} catch (error) {
		handleResponseError(error);
	}
	return { isSuccess: false };
};

export type QueryBasicOptions = {
	_defaulted?: UseQueryOptions["_defaulted"];
	_optimisticResults?: UseQueryOptions["_optimisticResults"];
	cacheTime?: UseQueryOptions["gcTime"];
	enabled?: boolean;
	initialDataUpdatedAt?: UseQueryOptions["initialDataUpdatedAt"];
	meta?: UseQueryOptions["meta"];
	networkMode?: UseQueryOptions["networkMode"];
	notifyOnChangeProps?: UseQueryOptions["notifyOnChangeProps"];
	refetchIntervalInBackground?: UseQueryOptions["refetchIntervalInBackground"];
	retryOnMount?: UseQueryOptions["retryOnMount"];
	staleTime?: number;
	refetchInterval?: number | false;
	refetchOnMount?: boolean | "always";
	refetchOnReconnect?: boolean | "always";
	refetchOnWindowFocus?: boolean | "always";
	retry?: boolean | number;
	retryDelay?: number;
	structuralSharing?: boolean;
	useErrorBoundary?: boolean;
};
